import { useState, useMemo } from "react";
import { X, Cable, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useI18n } from "@/context/I18nContext";

const RESERVE_BY_MODULE = { poles: "Słup", wells: "Studnia", closures: "Mufa", cabinets: "Szafa", onu: "Inny" };

export default function CableSlackDialog({ host, allCables, onClose, onSaved }) {
  const { t } = useI18n();
  const { settings } = useSettings();
  const { module, item, cables } = host;
  const passing = cables || [];
  const rememberSlack = !!settings.remember_last_slack;
  const lastSlack = (() => { try { return JSON.parse(localStorage.getItem("fp_last_slack") || "{}"); } catch { return {}; } })();
  const [showAll, setShowAll] = useState(passing.length === 0);
  const [cableId, setCableId] = useState(passing.length ? passing[0].id : "");
  const [meters, setMeters] = useState(rememberSlack && lastSlack.meters ? String(lastSlack.meters) : "");
  const [note, setNote] = useState(rememberSlack && lastSlack.note ? lastSlack.note : "");
  const [saving, setSaving] = useState(false);

  const hostLabel = item[Object.keys(item).includes("number") ? "number" : "name"] || item.number || item.name || "—";
  const reserveType = RESERVE_BY_MODULE[module] || "Inny";

  const options = useMemo(() => {
    if (showAll) {
      const passIds = new Set(passing.map((c) => c.id));
      const extra = (allCables || []).filter((c) => !passIds.has(c.id))
        .map((c) => ({ id: c.id, number: c.number, fiber_count: c.fiber_count }));
      return [...passing, ...extra];
    }
    return passing;
  }, [showAll, passing, allCables]);

  const save = async () => {
    if (!cableId) { toast.error(t("Wybierz kabel")); return; }
    const m = parseFloat(String(meters).replace(",", "."));
    if (!m || m <= 0) { toast.error(t("Podaj długość zapasu w metrach")); return; }
    setSaving(true);
    try {
      const { data: r } = await api.post("/cable-slack/add", {
        cable: cableId, length_m: m, reserve_type: reserveType, note,
        host_type: module, host_id: item.id,
        lat: item.lat, lng: item.lng,
      });
      const opt = r.optical;
      if (opt) {
        toast.success(`${t("Zapas dodany")} · ${t("długość optyczna kabla")}: ${opt.optical_length_m} m (${t("trasa")} ${opt.route_length_m} + ${t("zapasy")} ${opt.slack_length_m})`, { duration: 4500 });
      } else {
        toast.success(t("Zapas dodany"));
      }
      if (rememberSlack) { try { localStorage.setItem("fp_last_slack", JSON.stringify({ meters: m, note })); } catch { /* ignore */ } }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/50 p-4" data-testid="cable-slack-dialog">
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
          <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#F43F5E22" }}>
            <Cable size={16} className="text-rose-400" />
          </span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-100">{t("Dodaj zapas kabla")}</div>
            <div className="text-[11px] text-slate-400">{t(reserveType)} · {hostLabel}</div>
          </div>
          <button onClick={onClose} data-testid="cable-slack-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-300">{t("Kabel")}</label>
              {passing.length > 0 && (
                <button onClick={() => setShowAll((s) => !s)} data-testid="cable-slack-toggle-all"
                  className="text-[11px] text-cyan-400 hover:text-cyan-300 underline">
                  {showAll ? t("Tylko przechodzące") : t("Pokaż wszystkie kable")}
                </button>
              )}
            </div>
            {!showAll && passing.length > 0 && (
              <div className="mb-2 text-[11px] bg-cyan-500/10 border border-cyan-700/40 text-cyan-200 rounded-md px-2.5 py-1.5" data-testid="cable-slack-suggestion">
                {t("Podpowiedź")}: {t("najbliższy kabel")} <b>{passing[0].number || passing[0].id}</b>
                {passing[0].dist_m != null && ` (${passing[0].dist_m} m)`} — {t("zaznaczony automatycznie")}
              </div>
            )}
            {options.length === 0 ? (
              <div className="text-xs text-slate-500 border border-slate-800 rounded-md px-3 py-4 text-center">
                {t("Brak kabli w bazie")}
              </div>
            ) : (
              <div className="max-h-52 overflow-y-auto rounded-md border border-slate-800 divide-y divide-slate-800">
                {options.map((c) => (
                  <label key={c.id} data-testid={`cable-slack-opt-${c.id}`}
                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm ${cableId === c.id ? "bg-cyan-500/15" : "hover:bg-slate-800"}`}>
                    <input type="radio" name="slack-cable" checked={cableId === c.id} onChange={() => setCableId(c.id)}
                      className="accent-rose-500" />
                    <span className="text-slate-200 font-medium">{c.number || c.id}</span>
                    {c.fiber_count != null && <span className="text-[11px] text-slate-500">{c.fiber_count} {t("wł.")}</span>}
                    {c.dist_m != null && <span className="text-[11px] text-cyan-400" title={t("Odległość od punktu")}>{c.dist_m} m</span>}
                    {c.free != null && <span className="ml-auto text-[11px] text-slate-400">{c.free}/{c.fiber_count} {t("wolnych")}</span>}
                  </label>
                ))}
              </div>
            )}
            {passing.length > 0 && !showAll && (
              <div className="text-[11px] text-slate-500 mt-1">{t("Kable przechodzące przez ten punkt")}</div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-300 block mb-1.5">{t("Długość zapasu [m]")}</label>
            <input type="number" min="0" step="0.5" value={meters} onChange={(e) => setMeters(e.target.value)}
              data-testid="cable-slack-meters" autoFocus placeholder="np. 15"
              className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 focus:border-cyan-500 outline-none" />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-300 block mb-1.5">{t("Opis / lokalizacja")}</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              data-testid="cable-slack-note" placeholder={t("np. zwis na słupie, w studni")}
              className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 focus:border-cyan-500 outline-none resize-none" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-800">
          <button onClick={onClose} data-testid="cable-slack-cancel"
            className="px-3 py-1.5 text-sm text-slate-300 hover:text-white">{t("Anuluj")}</button>
          <button onClick={save} disabled={saving} data-testid="cable-slack-save"
            className="px-4 py-1.5 text-sm bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white rounded-md flex items-center gap-1.5">
            <Save size={14} /> {saving ? t("Zapisywanie…") : t("Zapisz zapas")}
          </button>
        </div>
      </div>
    </div>
  );
}
